import { useState, useEffect } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import i18n from '../../../../../../../i18';
import Richtext from '../../../../../../../components/richtext';
import CustomTextField from './CustomTextField';

const titleChartLimit = 180;

export const NotificationDetailsField = ({
    field,
    data,
    additionalData,
    onChange
}) => {
    const { id, label, needDivider } = field;
    const languages = additionalData;

    // Use data directly if already in correct format
    const [notificationDetails, setNotificationDetails] = useState(() => {
        
        // If data already contains ready notificationDetails structure - use as is
        if (data && typeof data === 'object' && !Array.isArray(data) && languages && languages.length > 0) {
            // Check if structure with language keys and title/text exists
            const hasLanguageStructure = languages.some(lang => 
                data[lang] && 
                typeof data[lang] === 'object' && 
                ('title' in data[lang] || 'text' in data[lang])
            );
            
            if (hasLanguageStructure) {
                return data; // Use data as is, no modification
            }
        }
        
        // If no languages - return empty object and wait
        if (!languages || languages.length === 0) {
            return {};
        }
        
        // Otherwise create empty structure
        return languages.reduce((details, lang) => {
            details[lang] = { title: '', text: '' };
            return details;
        }, {});
    });

    useEffect(() => {
        
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            // Check if structure with language keys and title/text exists
            const hasLanguageStructure = languages.some(lang => 
                data[lang] && 
                typeof data[lang] === 'object' && 
                ('title' in data[lang] || 'text' in data[lang])
            );
            
            if (hasLanguageStructure) {
                // Ensure all fields have values, even if they are undefined
                const cleanedData = {};
                languages.forEach(lang => {
                    cleanedData[lang] = {
                        title: data[lang]?.title || '',
                        text: data[lang]?.text || ''
                    };
                });
                setNotificationDetails(cleanedData);
                return;
            }
        }
        
        // If no data - set empty structure only if languages exist
        if (languages && languages.length > 0) {
            setNotificationDetails(
                languages.reduce((details, lang) => {
                    details[lang] = { title: '', text: '' };
                    return details;
                }, {})
            );
        }
    }, [data, languages]);

    // Ensure notificationDetails updates when data arrives
    useEffect(() => {
        if (data && notificationDetails && Object.keys(notificationDetails).length > 0) {
            // Check if data for this language is ready
            const needsUpdate = languages.some(lang => {
                const currentText = notificationDetails[lang]?.text;
                const newText = data[lang]?.text;
                return currentText !== newText;
            });
            
            if (needsUpdate && data !== notificationDetails) {
                setNotificationDetails({ ...data });
            }
        }
    }, [data, notificationDetails, languages]);

    const handleChange = (lang, fieldId, value) => {
        const updatedDetails = {
            ...notificationDetails,
            [lang]: {
                ...notificationDetails[lang],
                [fieldId]: value
            }
        };
        
        setNotificationDetails(updatedDetails);
        onChange(id, updatedDetails);
    };

    return (
        <div>
            {languages.map((lang, i) => {
                // Check if data for this language is ready
                if (!notificationDetails[lang]) {
                    return null;
                }
                
                const titleCharCount =
                    notificationDetails[lang]?.title.length || 0;
                return (
                    <div key={lang}>
                        <div style={{ marginTop: 24, marginBottom: 24 }}>
                            <Typography variant={'h5'} mb={2}>
                                {i18n.t(`enums.${lang}`)}
                            </Typography>

                            {/* Title Field */}

                            <CustomTextField
                                key={`title-${lang}`}
                                title={'notificationTitle'}
                                label={label}
                                lang={lang}
                                notificationDetails={notificationDetails}
                                handleChange={handleChange}
                                titleCharCount={titleCharCount}
                                titleChartLimit={titleChartLimit}
                            />

                            <Box mb={1}>
                                <Typography variant="body2" mb={1}>
                                    {i18n.t(`columns.notificationText`)}
                                </Typography>
                                <Richtext
                                    key={`richtext-${lang}`}
                                    toolbarButtons={[
                                        'bold',
                                        'italic',
                                        'underline'
                                    ]}
                                    enableEmojiPicker
                                    value={(() => {
                                        const textValue = notificationDetails[lang]?.text;
                                        
                                        // Create a simple Slate.js structure from plain text
                                        if (textValue && textValue !== '') {
                                            const slateValue = JSON.stringify([{
                                                type: 'paragraph',
                                                children: [{ text: textValue }]
                                            }]);
                                            return slateValue;
                                        }
                                        
                                        const emptySlateValue = JSON.stringify([{
                                            type: 'paragraph',
                                            children: [{ text: '' }]
                                        }]);
                                        return emptySlateValue;
                                    })()}
                                    onChange={(value) => {
                                        
                                        // Extract plain text from Slate.js structure
                                        try {
                                            const slateData = JSON.parse(value);
                                            const cleanText = slateData.map(node => {
                                                if (node.children && Array.isArray(node.children)) {
                                                    return node.children.map(child => child.text || '').join('');
                                                }
                                                return node.text || '';
                                            }).join('\n');
                                            
                                            handleChange(lang, 'text', cleanText);
                                        } catch (e) {
                                            handleChange(lang, 'text', '');
                                        }
                                    }}
                                />
                            </Box>
                        </div>
                    </div>
                );
            })}
            {needDivider && languages && languages.length > 0 && <Divider sx={{ mb: 2 }} />}
        </div>
    );
};
